import React, { useState, useCallback, useRef, useEffect, memo } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Handle,
  Position,
  type NodeProps,
  type Node,
  type Edge,
} from '@xyflow/react';
import { SoftwareResource, SoftwareResourceCluster, IbmEventEndpointMgmt } from '@core/icons-react';
import '@xyflow/react/dist/style.css';
import './PipelinePreview.scss';

type NodeVariant = 'application' | 'operation' | 'endpoint';

interface PipelineNodeData extends Record<string, unknown> {
  label: string;
  variant: NodeVariant;
  skeletonRows?: number;
}

const PipelineNode = memo(({ data }: NodeProps) => {
  const nodeData = data as PipelineNodeData;
  const { label, variant, skeletonRows = 0 } = nodeData;

  const getIcon = () => {
    switch (variant) {
      case 'application': return <SoftwareResource size={14} />;
      case 'operation': return <SoftwareResourceCluster size={14} />;
      case 'endpoint': return <IbmEventEndpointMgmt size={14} />;
      default: return <SoftwareResource size={14} />;
    }
  };

  return (
    <div className="pipeline-node">
      <Handle type="target" position={Position.Left} className="pipeline-node__handle" />
      
      <div className="pipeline-node__header">
        <span className="pipeline-node__icon">{getIcon()}</span>
        <span className="pipeline-node__label">{label}</span>
      </div>
      
      {skeletonRows > 0 && (
        <div className="pipeline-node__skeleton">
          {Array.from({ length: skeletonRows }).map((_, i) => (
            <div key={i} className="pipeline-node__skeleton-row">
              <span className="pipeline-node__skeleton-dot" />
              <span className="pipeline-node__skeleton-bar" />
            </div>
          ))}
        </div>
      )}
      
      <Handle type="source" position={Position.Right} className="pipeline-node__handle" />
    </div>
  );
});

PipelineNode.displayName = 'PipelineNode';

const INITIAL_NODES: Node<PipelineNodeData>[] = [
  // Scattered sources
  { id: 'a1', type: 'pipeline', position: { x: 0, y: 30 }, data: { label: 'User Events', variant: 'application', skeletonRows: 4 } },
  { id: 'a2', type: 'pipeline', position: { x: 30, y: 220 }, data: { label: 'Auth Logs', variant: 'application', skeletonRows: 3 } },
  
  // First ops - not aligned
  { id: 'o1', type: 'pipeline', position: { x: 190, y: 80 }, data: { label: 'Parse', variant: 'operation', skeletonRows: 2 } },
  
  // Branch point
  { id: 'o2', type: 'pipeline', position: { x: 340, y: 10 }, data: { label: 'Validate', variant: 'operation', skeletonRows: 5 } },
  { id: 'o3', type: 'pipeline', position: { x: 360, y: 170 }, data: { label: 'Enrich', variant: 'operation', skeletonRows: 6 } },
  
  // Isolated short chain at bottom
  { id: 'a3', type: 'pipeline', position: { x: 80, y: 380 }, data: { label: 'Metrics Raw', variant: 'application', skeletonRows: 3 } },
  { id: 'o4', type: 'pipeline', position: { x: 280, y: 350 }, data: { label: 'Aggregate', variant: 'operation', skeletonRows: 4 } },
  
  // Merge point
  { id: 'o5', type: 'pipeline', position: { x: 520, y: 90 }, data: { label: 'Join', variant: 'operation', skeletonRows: 7 } },
  
  // Final spread
  { id: 'e1', type: 'pipeline', position: { x: 700, y: 20 }, data: { label: '/analytics', variant: 'endpoint', skeletonRows: 3 } },
  { id: 'e2', type: 'pipeline', position: { x: 680, y: 180 }, data: { label: '/reports', variant: 'endpoint', skeletonRows: 5 } },
  
  // Separate bottom chain continues
  { id: 'o6', type: 'pipeline', position: { x: 480, y: 320 }, data: { label: 'Transform', variant: 'operation', skeletonRows: 4 } },
  { id: 'e3', type: 'pipeline', position: { x: 700, y: 340 }, data: { label: '/export', variant: 'endpoint', skeletonRows: 2 } },
];

const edgeStyle = { stroke: '#444', strokeWidth: 1.5 };

const INITIAL_EDGES: Edge[] = [
  // Top flow with branch
  { id: 'e1', source: 'a1', target: 'o1', type: 'straight', style: edgeStyle },
  { id: 'e2', source: 'o1', target: 'o2', type: 'straight', style: edgeStyle },
  { id: 'e3', source: 'o1', target: 'o3', type: 'straight', style: edgeStyle },
  
  // Auth logs merges in
  { id: 'e4', source: 'a2', target: 'o3', type: 'straight', style: edgeStyle },
  
  // Converge to join
  { id: 'e5', source: 'o2', target: 'o5', type: 'straight', style: edgeStyle },
  { id: 'e6', source: 'o3', target: 'o5', type: 'straight', style: edgeStyle },
  
  // Split to endpoints
  { id: 'e7', source: 'o5', target: 'e1', type: 'straight', style: edgeStyle },
  { id: 'e8', source: 'o5', target: 'e2', type: 'straight', style: edgeStyle },
  
  // Separate bottom chain
  { id: 'e9', source: 'a3', target: 'o4', type: 'straight', style: edgeStyle },
  { id: 'e10', source: 'o4', target: 'o6', type: 'straight', style: edgeStyle },
  { id: 'e11', source: 'o6', target: 'e3', type: 'straight', style: edgeStyle },
  
  // Cross connection
  { id: 'e12', source: 'o3', target: 'o6', type: 'straight', style: edgeStyle },
];

const nodeTypes = { pipeline: PipelineNode };

const PipelinePreviewInner: React.FC = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [panOffset, setPanOffset] = useState(0);
  const animationRef = useRef<number | null>(null);
  const flowRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  }, []);

  useEffect(() => {
    if (!isHovered) {
      const startOffset = panOffset;
      const startTime = performance.now();
      const duration = 800;

      const revert = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setPanOffset(startOffset * (1 - eased));

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(revert);
        }
      };

      animationRef.current = requestAnimationFrame(revert);
      return;
    }

    const startTime = performance.now();
    const duration = 12000;
    const maxPan = 300;

    const animate = (currentTime: number) => {
      if (!isHovered) return;

      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      setPanOffset(eased * maxPan);

      if (progress < 1 && isHovered) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isHovered]);

  return (
    <div
      className="pipeline-preview"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div 
        ref={flowRef}
        className="pipeline-preview__flow"
        style={{ transform: `translateX(-${panOffset}px)` }}
      >
        <ReactFlow
          nodes={INITIAL_NODES}
          edges={INITIAL_EDGES}
          nodeTypes={nodeTypes}
          defaultViewport={{ x: 30, y: 40, zoom: 0.68 }}
          defaultEdgeOptions={{ style: edgeStyle, type: 'straight' }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag={false}
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
          preventScrolling={false}
          proOptions={{ hideAttribution: true }}
        />
      </div>
      <div className="pipeline-preview__tint" />
      <div className="pipeline-preview__overlay" />
    </div>
  );
};

export const PipelinePreview: React.FC = () => {
  return (
    <ReactFlowProvider>
      <PipelinePreviewInner />
    </ReactFlowProvider>
  );
};
